import React, { useState, useEffect, useCallback } from 'react';
import SavingsRoundedIcon from '@mui/icons-material/SavingsRounded';
import MoreTimeRoundedIcon from '@mui/icons-material/MoreTimeRounded';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import TabbedPage from '../components/TabbedPage';
import SalaryAdvancesPage from './SalaryAdvancesPage';
import AdvanceRequestScreen from '../components/PayrollComps/AdvanceRequestScreen';
import OvertimeHost from '../components/PayrollComps/OvertimeHost';
import { selectEffectiveModules, selectUserTypeId, USER_TYPE } from '../redux/slices/authSlice';
import { hasModule } from '../data/serverModules';
import http from '../Api/http';
import { GetSalaryAdvancesDashboard, GetOvertimeDashboard } from '../Api/Api';

export default function PayAdjustmentsPage() {
    const isEmployee = useSelector(selectUserTypeId) === USER_TYPE.EMPLOYEE;
    const modules = useSelector(selectEffectiveModules);
    const [params] = useSearchParams();

    // The tab badges are the real pending counts from the server (they used to come from the
    // placeholder Redux data, so they showed "2" with nothing behind it).
    const [pending, setPending] = useState({ advances: 0, overtime: 0 });
    const loadCounts = useCallback(async () => {
        if (isEmployee) return;
        const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
        const [adv, ot] = await Promise.allSettled([
            hasModule(modules, 'advances') ? http.get(GetSalaryAdvancesDashboard) : Promise.resolve(null),
            hasModule(modules, 'overtime') ? http.get(GetOvertimeDashboard) : Promise.resolve(null),
        ]);
        const dataOf = (r) => (r.status === 'fulfilled' && r.value && !r.value.data?.error ? r.value.data?.data || {} : null);
        const a = dataOf(adv); const o = dataOf(ot);
        setPending((prev) => ({
            advances: a ? num(a.pendingRequests ?? (Array.isArray(a.advanceRequests) ? a.advanceRequests.length : 0)) : prev.advances,
            overtime: o ? num(o.pendingApprovals) : prev.overtime,
        }));
    }, [isEmployee, modules]);

    // On open, whenever the user switches tab (an action there may have changed a count), when the
    // browser tab is focused again, and every minute.
    useEffect(() => { loadCounts(); }, [loadCounts, params]);
    useEffect(() => {
        const t = setInterval(loadCounts, 60000);
        window.addEventListener('focus', loadCounts);
        return () => { clearInterval(t); window.removeEventListener('focus', loadCounts); };
    }, [loadCounts]);

    const tabs = [
        { key: 'advances', label: 'Salary Advances', icon: SavingsRoundedIcon, module: 'advances', badge: isEmployee ? 0 : pending.advances, render: () => (isEmployee ? <AdvanceRequestScreen /> : <SalaryAdvancesPage />) },
        { key: 'overtime', label: 'Overtime (OT)', icon: MoreTimeRoundedIcon, module: 'overtime', badge: isEmployee ? 0 : pending.overtime, render: () => <OvertimeHost /> },
    ];

    return <TabbedPage tabs={tabs} />;
}
